import useDocumentTitle from "@/components/ui/common/document-title";
import React, { useState } from "react";
import PaymentReportFilters from "./shared/payment-report-filters";
import PaymentChart from "./charts/payment-chart";
import PaymentReportTable from "./table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { useGetPaymentReportQuery } from "@/services/transactionApi";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { formatDate } from "@/lib/helper";

export default function PaymentReport() {
  useDocumentTitle("Payment Report");

  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;

  // Filter type that supports both single values and arrays
  type FilterState = {
    start_date?: string;
    end_date?: string;
    payment_method_ids?: (string | number)[];
    status?: string;
  };

  // Applied filters (what actually gets sent to the API)
  const [appliedFilters, setAppliedFilters] = useState<FilterState>({});

  // Pending filters (what user is selecting but hasn't applied yet)
  const [pendingFilters, setPendingFilters] = useState<FilterState>({});

  // Filter modal state
  const [openFilter, setOpenFilter] = useState(false);

  // Initial value for filters
  const initialValue = {
    limit: 10,
    offset: 0,
    sort_order: "desc",
    sort_key: "payment_method",
  };

  // Build query for charts/summary (fetch more data for visualization)
  const buildSummaryQuery = () => {
    const params = new URLSearchParams();
    params.append("offset", "0");
    params.append("sort_order", "desc");
    params.append("sort_key", "payment_method");

    // Add applied filters to query
    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (value) {
        // Handle arrays (multiselect) - append each value separately
        if (Array.isArray(value)) {
          value.forEach((val) => {
            if (val) {
              params.append(key, String(val));
            }
          });
        } else {
          params.append(key, String(value));
        }
      }
    });

    return params.toString();
  };

  // API call for charts/summary (only when on charts tab)
  const { data: summaryData, isLoading: summaryLoading } =
    useGetPaymentReportQuery(
      { query: buildSummaryQuery(), org_id: orgId },
    );

  const chartData = React.useMemo(() => {
    return Array.isArray(summaryData?.data) ? summaryData?.data : [];
  }, [summaryData]);

  // Handle date range change (pending state only)
  const handleDateRange = (dates: {
    start_date: Date | undefined;
    end_date: Date | undefined;
  }) => {
    const formattedStartDate = dates.start_date
      ? formatDate(dates.start_date)
      : "";
    const formattedEndDate = dates.end_date ? formatDate(dates.end_date) : "";

    setPendingFilters((prev) => ({
      ...prev,
      start_date: formattedStartDate || undefined,
      end_date: formattedEndDate || undefined,
    }));
  };

  // Apply filters - this is called when user clicks "Apply Filters"
  const applyFilters = () => {
    setAppliedFilters(pendingFilters);
    setOpenFilter(false);
  };

  // Reset filters
  const resetFilters = () => {
    setPendingFilters({});
    setAppliedFilters({});
    setOpenFilter(false);
  };

  return (
    <div className="w-full p-5 space-y-6">
      {/* Shared Filters Component */}
      <PaymentReportFilters
        dateRange={{
          start_date: pendingFilters.start_date,
          end_date: pendingFilters.end_date,
        }}
        appliedDateRange={{
          start_date: appliedFilters.start_date,
          end_date: appliedFilters.end_date,
        }}
        onDateRangeChange={handleDateRange}
        onResetFilters={resetFilters}
        onApplyFilters={applyFilters}
        filterData={pendingFilters}
        setFilterData={setPendingFilters}
        openFilter={openFilter}
        setOpenFilter={setOpenFilter}
        initialValue={initialValue}
        showAdvancedFilters={true}
      />

      {/* Charts Section */}
      <div className="space-y-6">
        <PaymentChart 
          data={chartData} 
          isLoading={summaryLoading} 
        />

        {/* Table Section */}
        <Card>
          <CardContent className="pt-6">
            <PaymentReportTable 
              appliedFilters={appliedFilters}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
